/**
 * Die Texte der Oberfläche in Deutsch und Englisch — ohne Abhängigkeit zum
 * Store, damit Store und Rundgang-Logik sie beim Laden schon haben (der Hook
 * `useT` in i18n.ts braucht den Store, deshalb liegt er getrennt).
 */
export type UiLocale = 'de' | 'en'

const DE = {
  // Chat
  subtitle: 'Assistentin im J4F VisionLab',
  greetingTitle: 'Wie kann ich dir helfen?',
  greetingText: 'Frag mich alles zu unseren Showcases — oder auch, was dich sonst interessiert.',
  starterLab: 'Was kann ich hier alles sehen?',
  starterJ4f: 'Was macht Just 4 Fashion?',
  placeholder: 'Frag K4ia etwas …',
  send: 'Senden',
  yourMessage: 'Deine Nachricht an K4ia',
  skipToInput: 'Zum Nachrichtenfeld springen',
  errorPrefix: 'Hoppla.',
  errorSuffix: 'Du kannst es gleich noch einmal versuchen.',
  photo: 'Foto',
  looking: 'Schaue …',
  camera: 'Kamera',
  cameraOn: 'Kamera an',
  suggestionsLabel: 'Vorschläge für die nächste Frage',
  // Neues Gespräch (Flo, 22.10.)
  newConversation: 'Neues Gespräch',
  newConversationConfirm:
    'Neues Gespräch anfangen? Der bisherige Verlauf verschwindet aus dem Chat.',
  resumeHint: 'Das ist dein Gespräch von {wann}.',
  resumeContinue: 'Weitermachen',
  resumeNew: 'Neu anfangen',
  resetHint: 'Neues Gespräch — dein letztes war {wann}.',
  dismiss: 'Schließen',
  // Avatar
  stateIdle: 'K4ia wartet',
  stateListening: 'K4ia hört zu',
  stateThinking: 'K4ia denkt nach',
  stateSpeaking: 'K4ia spricht',
  stateError: 'K4ia hat ein Problem festgestellt',
  // Sprachmodus
  voiceStart: 'Sprechen starten',
  voiceStop: 'Sprechen beenden',
  voiceConnecting: 'Verbinde …',
  voiceHint:
    'Tippen, sprechen, wieder tippen. Sobald K4ia antwortet, geht das Mikrofon von selbst aus.',
  voiceError: 'du kannst weiter per Text schreiben.',
  voiceBusy: 'Gerade sprechen schon viele Gäste mit K4ia. Schreib mir solange hier',
  cameraBusy: 'Die Kamera hat gerade viel zu tun. Sag mir einfach, wo du stehst:',
  cameraBusyNone: 'Die Kamera hat gerade viel zu tun. Frag mich einfach im Chat.',
  talkOpen: 'K4ia hört zu — tippen zum Beenden',
  talkClosed: 'Tippen zum Sprechen',
  listening: 'K4ia hört zu …',
  understanding: 'K4ia versteht dich …',
  // Gesprächsmodus (Vollbild)
  voiceModeOpen: 'Mit K4ia sprechen',
  voiceModeTalk: 'Sprechen',
  voiceModeEnd: 'Beenden',
  voiceMute: 'Stumm',
  voiceUnmute: 'Weiter',
  voiceModeConnecting: 'Verbinde …',
  voiceModeListening: 'Ich höre zu …',
  voiceModeHearing: 'Ich höre dich …',
  voiceModeUnderstood: 'Verstanden – einen Moment …',
  voiceModeThinking: 'Einen Moment …',
  voiceModeSpeaking: 'Tippen, um zu unterbrechen',
  voiceModeCardOpen: 'Die Karte unten bleibt offen, bis du sie abschickst.',
  voiceModeSpeakingBargeIn: 'Sprich einfach dazwischen',
  voiceModePaused: 'Pausiert — tippen zum Weitersprechen',
  voiceModeMicOn: 'Mikrofon an',
  voiceModeMicOff: 'Mikrofon aus',
  voiceModeBack: 'Zurück zum Chat',
  voiceModeInterrupt: 'K4ia unterbrechen',
  voiceModeResume: 'Weitersprechen',
  voiceNoMic: 'Mikrofon nicht freigegeben — du kannst per Text schreiben.',
  // Blase
  more: 'Mehr dazu',
  handOver: 'Ans Team weitergeben',
  yesPlease: 'Ja, bitte',
  noThanks: 'Nein, danke',
  confirmGeneric: 'Soll ich diese Aktion ausführen?',
  confirmLabels: {
    call_staff: 'Soll ich einen Mitarbeiter für dich rufen?',
    capture_lead: 'Soll ich deine Kontaktdaten für die Nachbereitung speichern?',
    book_meeting: 'Soll ich diesen Termin für dich buchen?',
    send_summary: 'Soll ich dir eine Zusammenfassung deines Besuchs per Mail schicken?',
    create_followup:
      'Soll ich dein Anliegen ans Team weitergeben, damit sich jemand bei dir meldet?',
  } as Record<string, string>,
  helpful: 'War das hilfreich?',
  openPdf: 'PDF öffnen',
  mediaFallback: 'Bild zum Showcase',
  helpfulYes: 'Antwort war hilfreich',
  helpfulNo: 'Antwort war nicht hilfreich',
  // Kontaktkarte
  cardTitle: 'Damit sich jemand vom Team bei dir melden kann',
  cardReason: 'Worum geht es?',
  cardReasonPlaceholder: 'z. B. Angebot für den digitalen Spiegel, Frage zum Preis …',
  cardMissingReason: 'worum es geht',
  cardName: 'Name',
  cardNamePlaceholder: 'Vor- und Nachname',
  cardCompany: 'Firma (optional)',
  cardCompanyPlaceholder: 'Firma',
  cardPhone: 'Telefon',
  cardEmail: 'E-Mail',
  cardEmailPlaceholder: 'name@firma.de',
  cardTime: 'Erreichbar (optional)',
  cardTimePlaceholder: 'z. B. vormittags, ab Montag',
  cardReady: 'Jemand vom Team meldet sich, in der Regel innerhalb von zwei Werktagen.',
  cardMissing: 'Es fehlt noch:',
  cardMissingName: 'Name',
  cardMissingContact: 'Telefon oder E-Mail',
  cardSubmit: 'Ans Team weitergeben',
  cardSubmitting: 'Wird weitergegeben …',
  cardDecline: 'Doch nicht',
  cardLabel: 'Kontaktkarte',
  and: 'und',
  // Rundgang-Chips und Zugtexte
  chipHere: 'Ich bin da',
  chipNext: 'Weiter zur nächsten Station',
  chipEnd: 'Rundgang beenden',
  chipNextShowcase: 'Nächster Showcase',
  chipTour: 'Führ mich durchs Lab',
  tourWalk: 'Rundgang: Weiter zu',
  tourArrived: 'Ich stehe vor:',
  tourEnd: 'Rundgang beenden',
  askMore: (title: string) => `Erzähl mir mehr über „${title}“.`,
  askFollowup: (title: string) =>
    `Ich möchte, dass sich jemand vom Team wegen „${title}“ bei mir meldet.`,
  tourFallback: 'Führ mich durch das J4F VisionLab.',
  // Einwilligung
  consentHello: 'Hallo, ich bin K4ia.',
  consentIntro:
    'Ich bin eine KI-Assistentin und beantworte deine Fragen zu den Showcases im J4F VisionLab — per Text, Sprache oder Kamera. Ein Gespräch mit mir ist kein Ersatz für ein Gespräch mit einem Menschen, aber ein guter Anfang.',
  consentData:
    'Deine Nachrichten werden zur Beantwortung verarbeitet, pseudonymisiert und nach 30 Tagen gelöscht. Kamerabilder und Audio werden nie gespeichert. Mikrofon und Kamera fragt dein Gerät erst, wenn du sie nutzt. Mehr dazu in unseren Datenschutzhinweisen.',
  consentGo: "Los geht's",
  consentNote: 'Mit „Los geht’s“ bestätigst du, dass du mit einer KI sprichst.',
}

const EN: typeof DE = {
  subtitle: 'Assistant at the J4F VisionLab',
  greetingTitle: 'How can I help you?',
  greetingText: 'Ask me anything about our showcases — or whatever else you are curious about.',
  starterLab: 'What can I see here?',
  starterJ4f: 'What does Just 4 Fashion do?',
  placeholder: 'Ask K4ia something …',
  send: 'Send',
  yourMessage: 'Your message to K4ia',
  skipToInput: 'Skip to the message field',
  errorPrefix: 'Oops.',
  errorSuffix: 'You can try again in a moment.',
  photo: 'Photo',
  looking: 'Looking …',
  camera: 'Camera',
  cameraOn: 'Camera on',
  suggestionsLabel: 'Suggestions for your next question',
  newConversation: 'New conversation',
  newConversationConfirm: 'Start a new conversation? The current history disappears from the chat.',
  resumeHint: 'This is your conversation from {wann}.',
  resumeContinue: 'Continue',
  resumeNew: 'Start over',
  resetHint: 'New conversation — your last one was {wann}.',
  dismiss: 'Dismiss',
  stateIdle: 'K4ia is waiting',
  stateListening: 'K4ia is listening',
  stateThinking: 'K4ia is thinking',
  stateSpeaking: 'K4ia is speaking',
  stateError: 'K4ia ran into a problem',
  voiceStart: 'Start talking',
  voiceStop: 'Stop talking',
  voiceConnecting: 'Connecting …',
  voiceHint: 'Tap, speak, tap again. As soon as K4ia answers, the microphone closes by itself.',
  voiceError: 'you can keep typing.',
  voiceBusy: 'Many guests are talking to K4ia right now. Type to me here in the meantime',
  cameraBusy: 'The camera is very busy right now. Just tell me where you are:',
  cameraBusyNone: 'The camera is very busy right now. Just ask me in the chat.',
  talkOpen: 'K4ia is listening — tap to finish',
  talkClosed: 'Tap to speak',
  listening: 'K4ia is listening …',
  understanding: 'K4ia understands you …',
  voiceModeOpen: 'Talk to K4ia',
  voiceModeTalk: 'Talk',
  voiceModeEnd: 'End',
  voiceMute: 'Mute',
  voiceUnmute: 'Resume',
  voiceModeConnecting: 'Connecting …',
  voiceModeListening: 'Listening …',
  voiceModeHearing: 'I can hear you …',
  voiceModeUnderstood: 'Got it – one moment …',
  voiceModeThinking: 'One moment …',
  voiceModeSpeaking: 'Tap to interrupt',
  voiceModeCardOpen: 'The card below stays open until you send it.',
  voiceModeSpeakingBargeIn: 'Just talk to interrupt',
  voiceModePaused: 'Paused — tap to keep talking',
  voiceModeMicOn: 'Microphone on',
  voiceModeMicOff: 'Microphone off',
  voiceModeBack: 'Back to chat',
  voiceModeInterrupt: 'Interrupt K4ia',
  voiceModeResume: 'Keep talking',
  voiceNoMic: 'Microphone not allowed — you can type instead.',
  more: 'Tell me more',
  handOver: 'Hand over to the team',
  yesPlease: 'Yes, please',
  noThanks: 'No, thanks',
  confirmGeneric: 'Shall I go ahead with this?',
  confirmLabels: {
    call_staff: 'Shall I call a team member for you?',
    capture_lead: 'Shall I save your contact details for a follow-up?',
    book_meeting: 'Shall I book this appointment for you?',
    send_summary: 'Shall I email you a summary of your visit?',
    create_followup: 'Shall I hand your request over to the team so someone gets back to you?',
  },
  helpful: 'Was this helpful?',
  openPdf: 'Open PDF',
  mediaFallback: 'Showcase image',
  helpfulYes: 'Answer was helpful',
  helpfulNo: 'Answer was not helpful',
  cardTitle: 'So someone from the team can get back to you',
  cardReason: 'What is it about?',
  cardReasonPlaceholder: 'e.g. quote for the digital mirror, question about pricing …',
  cardMissingReason: 'what it is about',
  cardName: 'Name',
  cardNamePlaceholder: 'First and last name',
  cardCompany: 'Company (optional)',
  cardCompanyPlaceholder: 'Company',
  cardPhone: 'Phone',
  cardEmail: 'Email',
  cardEmailPlaceholder: 'name@company.com',
  cardTime: 'Best time to reach you (optional)',
  cardTimePlaceholder: 'e.g. mornings, from Monday',
  cardReady: 'Someone from the team will get back to you, usually within two working days.',
  cardMissing: 'Still missing:',
  cardMissingName: 'name',
  cardMissingContact: 'phone or email',
  cardSubmit: 'Hand over to the team',
  cardSubmitting: 'Handing over …',
  cardDecline: 'Never mind',
  cardLabel: 'Contact card',
  and: 'and',
  chipHere: "I'm here",
  chipNext: 'On to the next station',
  chipEnd: 'End the tour',
  chipNextShowcase: 'Next showcase',
  chipTour: 'Show me around the lab',
  tourWalk: 'Tour: on to',
  tourArrived: "I'm standing at:",
  tourEnd: 'End the tour',
  askMore: (title: string) => `Tell me more about “${title}”.`,
  askFollowup: (title: string) =>
    `I'd like someone from the team to get back to me about “${title}”.`,
  tourFallback: 'Show me around the J4F VisionLab.',
  consentHello: "Hello, I'm K4ia.",
  consentIntro:
    "I'm an AI assistant and I answer your questions about the showcases at the J4F VisionLab — by text, voice or camera. Talking to me is no substitute for talking to a person, but it is a good start.",
  consentData:
    'Your messages are processed to answer you, pseudonymised and deleted after 30 days. Camera images and audio are never stored. Your device asks for microphone and camera only when you use them. More in our privacy notice.',
  consentGo: "Let's go",
  consentNote: 'By tapping “Let’s go” you confirm that you are talking to an AI.',
}

export type UiStrings = typeof DE
export const STRINGS: Record<UiLocale, UiStrings> = { de: DE, en: EN }

export function strings(locale: UiLocale): UiStrings {
  return STRINGS[locale]
}
